import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export const SheetMissingList = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try { const { data } = await api.get("/fees/sheets/tabs/missing"); setItems(data.items); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, path, ok) => {
    setBusy(id + path);
    try {
      const { data } = await api.post(`/fees/sheets/tabs/${id}/${path}`);
      if (path === "write-now") toast[data.ok ? "success" : "error"](data.ok ? "Written to the fee tab" : "Could not write — please enter it by hand");
      else toast.success(ok);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(""); }
  };
  const hand = (m) => window.confirm(`Mark ${m.student_name} (${m.roll_number}) ${inr(m.amount)} as entered by hand?\nThe app will never write this payment to the sheet again.`) && act(m.id, "by-hand", "Marked as entered by hand");

  return (
    <div className="rounded-xl border border-red-200 bg-red-50/40 p-3 space-y-2" data-testid="sheet-missing">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <h5 className="font-semibold text-slate-800 text-sm">Not in Google Sheet {items ? `(${items.length})` : ""}</h5>
        <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={load} data-testid="sheet-missing-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      {items === null ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : items.length === 0 ? (
        <p className="text-xs text-green-700 flex items-center gap-1" data-testid="sheet-missing-none"><CheckCircle2 className="h-3.5 w-3.5" /> Every approved payment is in your fee tabs.</p>
      ) : items.map((m, i) => (
        <div key={m.id} className="rounded-lg border border-red-200 bg-white p-2 text-xs flex flex-wrap items-start gap-3" data-testid={`sheet-missing-${i}`}>
          <div className="flex-1 min-w-[220px] space-y-0.5">
            <div className="font-semibold text-slate-900">{m.student_name} · {m.roll_number} · {inr(m.amount)}{m.split ? ` (Cash ${inr(m.split.cash)} + UPI ${inr(m.split.upi)})` : ""}</div>
            <div className="text-slate-600">Month(s): {m.months || "—"} · Fee tab: <b>{m.tab || "—"}</b></div>
            <div className="text-slate-500">Receipt {m.receipt_no || "—"} · {formatDateTime(m.receipt_at)} · <span className="text-red-700">{m.reason}</span></div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act(m.id, "write-now")} data-testid={`sheet-missing-write-${i}`}>Write now</Button>
            <Button size="sm" className="bg-green-700 hover:bg-green-800" disabled={!!busy} onClick={() => hand(m)} data-testid={`sheet-missing-hand-${i}`}>Entered by hand</Button>
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-500">Interrupted writes are retried automatically every 15 minutes. Anything that still fails is emailed to you and listed here. Already typed it yourself? Press <b>Entered by hand</b> first so it is never written twice.</p>
    </div>
  );
};
